import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";

const A = "https://customer-assets.emergentagent.com/job_adrien-sebert-dj/artifacts/";

const PHOTOS = [
  { src: `${A}9zklpmt9_6B1ACEDF-F411-4E35-90BF-FEFE57AF2AED.PNG`, caption: "Show confettis · foule en délire", tag: "Scène", span: "md:col-span-2 md:row-span-2" },
  { src: `${A}ujbx7xzc_DSC04775.JPG`, caption: "Ouverture de bal", tag: "Mariage" },
  { src: `${A}kgg70tb4_IMG_8909.jpeg`, caption: "Dancefloor anniversaire", tag: "Anniversaire" },
  { src: `${A}fh2an4yw_WhatsApp%20Image%202026-05-12%20at%2011.34.jpeg`, caption: "Étincelles froides", tag: "Effets", span: "md:row-span-2" },
  { src: `${A}z7ohc5uw_IMG_8907.jpeg`, caption: "Soirée privée", tag: "Privé" },
  { src: `${A}ceq4aa8n_dsc08243_3_299052-176999564712674.jpeg`, caption: "Sonorisation & éclairage", tag: "Technique" },
  { src: `${A}4ddkhmgi_WhatsApp%20Image%202026-05-12%20at%2011.34.59.jpeg`, caption: "Mise en lumière LED", tag: "Lumière", span: "md:col-span-2" },
  { src: `${A}yeuegh8j_IMG_8908.jpeg`, caption: "Fumée lourde au sol", tag: "Effets" },
  { src: `${A}8ukozayz_IMG_9795.jpeg`, caption: "Adrien en cabine", tag: "Portrait" },
];

const Lightbox = ({ index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);

  const p = PHOTOS[index];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="gallery-lightbox"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        aria-label="Fermer"
        data-testid="gallery-lightbox-close"
        className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-black text-white flex items-center justify-center transition-colors"
      >
        <X size={20} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Photo précédente"
        data-testid="gallery-lightbox-prev"
        className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-black text-white flex items-center justify-center transition-colors"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Photo suivante"
        data-testid="gallery-lightbox-next"
        className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-black text-white flex items-center justify-center transition-colors"
      >
        <ChevronRight size={22} />
      </button>

      <motion.figure
        key={index}
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="max-w-6xl w-full max-h-full flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={p.src}
          alt={p.caption}
          data-testid="gallery-lightbox-image"
          className="max-h-[78vh] w-auto max-w-full object-contain rounded-2xl border border-white/10 shadow-2xl"
        />
        <figcaption className="mt-4 flex items-center gap-3 text-sm">
          <span className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">{p.tag}</span>
          <span className="text-white">{p.caption}</span>
          <span className="text-zinc-500">{index + 1} / {PHOTOS.length}</span>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
};

export const Gallery = () => {
  const [active, setActive] = useState(null);
  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i - 1 + PHOTOS.length) % PHOTOS.length), []);
  const next = useCallback(() => setActive((i) => (i + 1) % PHOTOS.length), []);

  return (
    <section
      id="galerie"
      data-testid="gallery-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[var(--bg-surface)] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_10%,rgba(212,175,55,0.10),transparent_55%)]" />
      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-12"
        >
          <span className="eyebrow inline-flex items-center gap-2">
            <Camera size={13} /> Galerie
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            Imaginez <span className="gold-text-gradient">votre soirée.</span>
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed">
            Quelques instants captés lors de mes prestations : mariages, anniversaires,
            effets scéniques et ambiances lumineuses. Cliquez pour agrandir.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[160px] md:auto-rows-[200px] gap-3 md:gap-4">
          {PHOTOS.map((p, i) => (
            <motion.button
              key={p.src}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`gallery-item-${i}`}
              aria-label={`Agrandir : ${p.caption}`}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 text-left ${p.span || ""}`}
            >
              <img
                src={p.src}
                alt={p.caption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Maximize2 size={14} />
              </div>
              <div className="absolute bottom-3 left-3 right-3">
                <div className="text-[9px] tracking-[0.28em] uppercase text-[#D4AF37]">{p.tag}</div>
                <div className="text-white text-xs md:text-sm font-medium leading-tight">{p.caption}</div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active !== null && <Lightbox index={active} onClose={close} onPrev={prev} onNext={next} />}
      </AnimatePresence>
    </section>
  );
};
